"use client"

import { yupResolver } from "@hookform/resolvers/yup"
import moment from "moment"
import { type FC, useContext, useEffect, useMemo } from "react"
import { type Control, type FieldValues, useForm } from "react-hook-form"
import * as yup from "yup"

import { UserInfoField, useUserUpdateAuthMutation } from "../../generated/graphql"
import { graphQLClient } from "../../graphql/client"
import { AppContext } from "../../providers/AppProvider"
import { UserContext } from "../../providers/UserProvider"
import { processException } from "../../utils/errors"
import { DatePickerField } from "../DatePickerField"
import { InputField } from "../InputField"
import UbiqButton, { UbiqButtonType } from "../UbiqButton"

interface FormInputs {
  firstName: string
  lastName: string
  email: string
  confirmEmail: string
  username: string
  dob: Date | null
}

type FormFieldName = keyof FormInputs

const USER_INFO_TO_FORM_KEY: Partial<Record<UserInfoField, FormFieldName>> = {
  [UserInfoField.FirstName]: "firstName",
  [UserInfoField.LastName]: "lastName",
  [UserInfoField.Username]: "username",
  [UserInfoField.Email]: "email",
  [UserInfoField.Dob]: "dob",
}

const FIELD_ORDER: FormFieldName[] = [
  "firstName",
  "lastName",
  "username",
  "email",
  "confirmEmail",
  "dob",
]
const TERMS_LINK_BUTTON_STYLES =
  "cursor-pointer underline appearance-none border-0 bg-transparent p-0"

const capitalizeNameInput = (value: string) =>
  value.replace(/(^|[\s'-])[a-zà-ÿ]/g, (match) => match.toUpperCase())

const trimStringValue = (value: unknown) => (typeof value === "string" ? value.trim() : value)

// Individual yup validators per field
const fieldValidators: Record<FormFieldName, yup.AnySchema> = {
  firstName: yup
    .string()
    .transform(trimStringValue)
    .required("First name is required")
    .min(2, "First name should be at least 2 characters")
    .max(30, "First name cannot exceed 30 characters")
    .matches(/^[a-zA-ZÀ-ÿ\s-]+$/, "Only letters, spaces, and hyphens are allowed"),
  lastName: yup
    .string()
    .transform(trimStringValue)
    .required("Last name is required")
    .min(2, "Last name should be at least 2 characters")
    .max(30, "Last name cannot exceed 30 characters")
    .matches(/^[a-zA-ZÀ-ÿ\s-]+$/, "Only letters, spaces, and hyphens are allowed"),
  username: yup
    .string()
    .required("Username is required")
    .min(3, "Username must be at least 3 characters")
    .max(30, "Username must be at most 30 characters")
    .matches(
      /^[a-z][a-z0-9_.]*$/,
      "Username must start with a letter and can only contain lowercase letters, numbers, underscores, and dots",
    ),
  email: yup
    .string()
    .transform(trimStringValue)
    .required("Email is required")
    .email("Please enter a valid email"),
  confirmEmail: yup.string(),
  dob: yup
    .date()
    .nullable()
    .required("Date of birth is required")
    .test("is-valid-age", "You must be at least 13 years old", (value) => {
      if (!value) return false
      const age = moment().diff(moment(value), "years")
      return age >= 13
    }),
}

const optionalStringField = (validator: yup.StringSchema<string | undefined | null>) =>
  validator
    .transform((value, originalValue) => {
      if (typeof originalValue === "string" && originalValue.trim() === "") {
        return undefined
      }
      return value
    })
    .notRequired()

const optionalFieldValidators: Record<FormFieldName, yup.AnySchema> = {
  firstName: optionalStringField(
    yup
      .string()
      .transform(trimStringValue)
      .min(2, "First name should be at least 2 characters")
      .max(30, "First name cannot exceed 30 characters")
      .matches(/^[a-zA-ZÀ-ÿ\s-]+$/, "Only letters, spaces, and hyphens are allowed"),
  ),
  lastName: optionalStringField(
    yup
      .string()
      .transform(trimStringValue)
      .min(2, "Last name should be at least 2 characters")
      .max(30, "Last name cannot exceed 30 characters")
      .matches(/^[a-zA-ZÀ-ÿ\s-]+$/, "Only letters, spaces, and hyphens are allowed"),
  ),
  username: optionalStringField(
    yup
      .string()
      .min(3, "Username must be at least 3 characters")
      .max(30, "Username must be at most 30 characters")
      .matches(
        /^[a-z][a-z0-9_.]*$/,
        "Username must start with a letter and can only contain lowercase letters, numbers, underscores, and dots",
      ),
  ),
  email: optionalStringField(
    yup.string().transform(trimStringValue).email("Please enter a valid email"),
  ),
  confirmEmail: yup.string().notRequired(),
  dob: yup
    .date()
    .nullable()
    .notRequired()
    .test("is-valid-age-optional", "You must be at least 13 years old", (value) => {
      if (!value) return true
      const age = moment().diff(moment(value), "years")
      return age >= 13
    }),
}

interface CompleteAccountFormProps {
  requiredFields?: UserInfoField[]
  onComplete: (email?: string) => void | Promise<void>
  requireEmailConfirmation?: boolean
  sendEmailVerification?: boolean
  syncUserOnSubmit?: boolean
  showIntro?: boolean
  showTerms?: boolean
  submitLabel?: string
  submitButtonType?: UbiqButtonType
  submitButtonStyles?: string
  containerClassName?: string
  fieldsContainerClassName?: string
  fieldErrorsInFlow?: boolean
}

export const CompleteAccountForm: FC<CompleteAccountFormProps> = ({
  requiredFields,
  onComplete,
  requireEmailConfirmation = false,
  sendEmailVerification = true,
  syncUserOnSubmit = true,
  showIntro = true,
  showTerms = true,
  submitLabel = "Register",
  submitButtonType = UbiqButtonType.LIGHT,
  submitButtonStyles = "!self-start mb-4 !w-fit",
  containerClassName = "",
  fieldsContainerClassName = "flex flex-col gap-4 max-w-[455px]",
  fieldErrorsInFlow = false,
}) => {
  const { setUser, user } = useContext(UserContext)
  const { mutateAsync: userUpdate } = useUserUpdateAuthMutation(graphQLClient)
  const { showToSModal, showPPModal } = useContext(AppContext)
  const hasExistingUsername = !!(user?.username && user.username.trim().length > 0)
  const normalizedCurrentEmail = user?.email?.trim().toLowerCase() ?? ""

  const renderedFormFields: FormFieldName[] = FIELD_ORDER

  const requiredFormFields: FormFieldName[] = useMemo(() => {
    if (!requiredFields) return renderedFormFields
    const mappedRequiredFields = requiredFields
      .map((f) => USER_INFO_TO_FORM_KEY[f])
      .filter((f): f is FormFieldName => !!f)
      .filter((f) => renderedFormFields.includes(f))
      .sort((a, b) => FIELD_ORDER.indexOf(a) - FIELD_ORDER.indexOf(b))
    return mappedRequiredFields
  }, [requiredFields, renderedFormFields])

  // Dynamic validation schema — validate all rendered fields, require only requiredFormFields
  const validationSchema = useMemo(() => {
    const shape: Record<string, yup.AnySchema> = {}
    for (const field of renderedFormFields) {
      if (field === "confirmEmail") {
        shape[field] = yup
          .string()
          .test("confirm-email", "Emails must match", function validateConfirmEmail(value) {
            if (!requireEmailConfirmation) {
              return true
            }

            const emailValue = typeof this.parent.email === "string" ? this.parent.email.trim() : ""
            const normalizedEmailValue = emailValue.toLowerCase()
            const shouldRequireConfirm =
              normalizedCurrentEmail.length === 0 || normalizedEmailValue !== normalizedCurrentEmail

            if (!shouldRequireConfirm) {
              return true
            }

            if (!value?.trim()) {
              return this.createError({ message: "Please confirm your email" })
            }

            return (
              value.trim().toLowerCase() === normalizedEmailValue ||
              this.createError({ message: "Emails must match" })
            )
          })
        continue
      }

      shape[field] = requiredFormFields.includes(field)
        ? fieldValidators[field]
        : optionalFieldValidators[field]
    }
    return yup.object().shape(shape)
  }, [normalizedCurrentEmail, renderedFormFields, requireEmailConfirmation, requiredFormFields])

  // Field IDs for keyboard navigation
  const fieldIds = useMemo(() => {
    const ids: Record<string, string> = {}
    for (const f of renderedFormFields) {
      ids[f] = `${f}-input`
    }
    return ids
  }, [renderedFormFields])

  const defaultValues: FormInputs = useMemo(
    () => ({
      dob: user?.birthday ? new Date(user.birthday) : null,
      confirmEmail: "",
      email: user?.email?.trim() || "",
      firstName: user?.firstName?.trim() || "",
      lastName: user?.lastName?.trim() || "",
      username: user?.username || "",
    }),
    [user?.birthday, user?.email, user?.firstName, user?.lastName, user?.username],
  )

  const {
    control,
    setError,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: yupResolver(validationSchema),
  })

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  const currentEmailValue = watch("email") ?? ""
  const normalizedEnteredEmail = currentEmailValue.trim().toLowerCase()
  const shouldShowConfirmEmail =
    requireEmailConfirmation &&
    (normalizedCurrentEmail.length === 0 || normalizedEnteredEmail !== normalizedCurrentEmail)

  const visibleFormFields = useMemo(
    () => renderedFormFields.filter((field) => field !== "confirmEmail" || shouldShowConfirmEmail),
    [renderedFormFields, shouldShowConfirmEmail],
  )

  useEffect(() => {
    if (!shouldShowConfirmEmail) {
      clearErrors("confirmEmail")
      setValue("confirmEmail", "")
    }
  }, [clearErrors, setValue, shouldShowConfirmEmail])

  const fieldControl = control as unknown as Control<FieldValues>

  const handleFieldChange = (fieldName: FormFieldName, value: string) => {
    clearErrors(fieldName)
    const nextValue =
      fieldName === "firstName" || fieldName === "lastName" ? capitalizeNameInput(value) : value
    setValue(fieldName, nextValue)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, currentField: FormFieldName) => {
    if (e.key !== "Enter") return
    e.preventDefault()
    const currentIndex = visibleFormFields.indexOf(currentField)
    const nextField = visibleFormFields[currentIndex + 1]
    if (nextField) {
      document.getElementById(fieldIds[nextField])?.focus()
    } else {
      void handleSubmit(onSubmit)()
    }
  }

  const onSubmit = async (data: FormInputs) => {
    try {
      const firstName = data.firstName?.trim() || undefined
      const lastName = data.lastName?.trim() || undefined
      const username = hasExistingUsername ? undefined : data.username?.trim() || undefined
      const email = data.email?.trim() || undefined
      const currentEmail = user?.email?.trim() ?? ""
      const isEmailChanged = email?.toLowerCase() !== currentEmail.toLowerCase()
      const shouldSendEmailUpdate = !!email && isEmailChanged
      const effectiveEmail = email ?? currentEmail

      const updatedData = await userUpdate({
        birthday: data.dob?.toISOString(),
        email: shouldSendEmailUpdate ? email : undefined,
        firstName,
        lastName,
        sendEmailVerification,
        username,
      })

      if (updatedData.userUpdate?.id && user && syncUserOnSubmit) {
        setUser({
          ...user,
          birthday: data.dob ?? user.birthday,
          email: effectiveEmail,
          emailVerified: shouldSendEmailUpdate ? false : user.emailVerified,
          firstName: firstName ?? user.firstName,
          lastName: lastName ?? user.lastName,
          username: username ?? user.username,
        })
      }

      await onComplete(effectiveEmail)
    } catch (err: unknown) {
      processException(err)

      if (err && typeof err === "object" && "response" in err) {
        const response = err.response as {
          errors?: Array<{ extensions?: { path?: string }; message?: string }>
        }
        const errors = response.errors

        if (errors) {
          const emailError = errors.find(
            (error) =>
              error.extensions?.path === "email" ||
              error.message?.toLowerCase().includes("email already in use"),
          )

          const usernameError = errors.find(
            (error) =>
              error.extensions?.path === "username" ||
              error.message?.toLowerCase().includes("username is already taken") ||
              error.message?.toLowerCase().includes("username already in use"),
          )

          if (emailError) {
            setError("email", { message: "This email is already taken" })
            return
          }

          if (usernameError) {
            setError("username", { message: "This username is already taken" })
            return
          }
        }
      }

      if (err instanceof Error) {
        if (err.message.toLowerCase().includes("email")) {
          setError("email", { message: "This email is already taken" })
        } else if (err.message.toLowerCase().includes("username")) {
          setError("username", { message: "This username is already taken" })
        } else {
          setError("root", { message: "Something went wrong, please try again" })
        }
      } else {
        setError("root", { message: "Something went wrong, please try again" })
      }
    }
  }

  const isFieldVisible = (field: FormFieldName) => visibleFormFields.includes(field)

  return (
    <div className={containerClassName}>
      {showIntro && (
        <p className="text-body-lg text-textPrimary mb-4 max-w-[580px]">
          You don't seem to have a complete account with that phone number. Please follow the steps
          and complete your account below.
        </p>
      )}
      <form className="flex flex-col" onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
        <div className={fieldsContainerClassName}>
          {isFieldVisible("firstName") && (
            <InputField
              additionalProps={{
                autoCapitalize: "words",
                id: fieldIds.firstName,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  handleFieldChange("firstName", e.target.value)
                },
                onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                  handleKeyDown(e, "firstName")
                },
              }}
              className="bg-transparent"
              control={fieldControl}
              error={errors.firstName?.message}
              errorInFlow={fieldErrorsInFlow}
              name="firstName"
              placeholder="First name"
            />
          )}
          {isFieldVisible("lastName") && (
            <InputField
              additionalProps={{
                autoCapitalize: "words",
                id: fieldIds.lastName,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  handleFieldChange("lastName", e.target.value)
                },
                onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                  handleKeyDown(e, "lastName")
                },
              }}
              className="bg-transparent"
              control={fieldControl}
              error={errors.lastName?.message}
              errorInFlow={fieldErrorsInFlow}
              name="lastName"
              placeholder="Last name"
            />
          )}
          {isFieldVisible("username") && (
            <InputField
              additionalProps={{
                autoCapitalize: "off",
                autoComplete: "off",
                disabled: hasExistingUsername,
                id: fieldIds.username,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  handleFieldChange("username", e.target.value)
                },
                onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                  handleKeyDown(e, "username")
                },
              }}
              className="bg-transparent"
              control={fieldControl}
              error={errors.username?.message}
              errorInFlow={fieldErrorsInFlow}
              name="username"
              placeholder="Username"
            />
          )}
          {isFieldVisible("email") && (
            <InputField
              additionalProps={{
                autoCapitalize: "none",
                autoComplete: "email",
                id: fieldIds.email,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  handleFieldChange("email", e.target.value)
                },
                onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                  handleKeyDown(e, "email")
                },
              }}
              additionalState={
                sendEmailVerification && user?.email && !user?.emailVerified ? "not verified" : ""
              }
              className="bg-transparent"
              control={fieldControl}
              error={errors.email?.message}
              errorInFlow={fieldErrorsInFlow}
              name="email"
              placeholder="Email"
              type="email"
            />
          )}
          {isFieldVisible("confirmEmail") && (
            <InputField
              additionalProps={{
                autoCapitalize: "none",
                autoComplete: "email",
                id: fieldIds.confirmEmail,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  handleFieldChange("confirmEmail", e.target.value)
                },
                onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                  handleKeyDown(e, "confirmEmail")
                },
              }}
              className="bg-transparent"
              control={fieldControl}
              error={errors.confirmEmail?.message}
              errorInFlow={fieldErrorsInFlow}
              name="confirmEmail"
              placeholder="Confirm email"
              type="email"
            />
          )}
          {isFieldVisible("dob") && (
            <DatePickerField
              additionalProps={{
                id: fieldIds.dob,
                onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                  handleKeyDown(e, "dob")
                },
              }}
              control={fieldControl}
              error={errors.dob?.message}
              errorInFlow={fieldErrorsInFlow}
              name="dob"
              placeholder="Date of birth"
            />
          )}
        </div>
        {showTerms && (
          <p className="text-body-lg text-textPrimary my-4 max-w-[580px]">
            By signing up you create an account on the community platform WERZ and accept the{" "}
            <button
              className={TERMS_LINK_BUTTON_STYLES}
              onClick={() => showToSModal()}
              type="button"
            >
              Terms
            </button>
            {` and `}
            <button
              className={TERMS_LINK_BUTTON_STYLES}
              onClick={() => showPPModal()}
              type="button"
            >
              Privacy Policy
            </button>
            .
          </p>
        )}
        <UbiqButton
          buttonType={submitButtonType}
          disabled={isSubmitting}
          styles={submitButtonStyles}
          title={submitLabel}
          type="submit"
        />
        {errors.root && <p className="mt-2 text-body-sm text-error">{errors.root.message}</p>}
      </form>
    </div>
  )
}
